import React, { useState } from 'react';
import { 
  FaCreditCard, FaPercentage, FaShieldAlt, 
  FaPlus, FaTrash, FaCheckCircle, FaLightbulb, 
  FaArrowRight, FaClock
} from 'react-icons/fa';

const CreditCardTracker = ({ creditCard, setCreditCard, config }) => {
  const [isAddTxModalOpen, setIsAddTxModalOpen] = useState(false);
  const [newTx, setNewTx] = useState({
    date: config.referenceDate || new Date().toISOString().split('T')[0],
    type: 'New Loan / Spend',
    amount: '',
    gracePeriodDays: 30,
    notes: ''
  });

  // Safe numerical extraction
  const balance = parseFloat(creditCard.balance) || 0;
  const lastInterest = parseFloat(creditCard.lastInterest) || 0;
  const plannedRepayment = parseFloat(creditCard.plannedRepayment) || 0;
  const gracePeriodDays = parseInt(creditCard.gracePeriodDays, 10) || 30;

  // Implied Rates calculations
  const impliedMonthlyRate = balance > 0 ? (lastInterest / balance) : 0.009559;
  const impliedMonthlyRatePct = impliedMonthlyRate * 100;
  const impliedAPR = impliedMonthlyRatePct * 12;
  const dailyPeriodicRate = impliedMonthlyRatePct / 30.417;

  // Projected outcomes based on plannedRepayment
  const effectiveRemaining = Math.max(0, balance - plannedRepayment);
  const projectedNextInterest = effectiveRemaining * impliedMonthlyRate;
  const estimatedSavings = Math.min(balance, plannedRepayment) * impliedMonthlyRate;

  // Format currency helper
  const fmt = (num) => {
    return '$' + (parseFloat(num) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Pre-configured scenario repayment amounts
  const scenarioTiers = [
    0,
    500,
    1000,
    1500,
    2000,
    3000,
    Math.round(balance * 100) / 100
  ].filter((v, i, a) => a.indexOf(v) === i && v <= balance + 100).sort((a, b) => a - b);

  // Update a single card parameter
  const updateCardField = (field, value) => {
    setCreditCard(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Add new transaction
  const handleAddTransaction = (e) => {
    e.preventDefault();
    if (!newTx.amount) return;

    const amt = parseFloat(newTx.amount);
    const isRepayment = newTx.type === 'Repayment';
    const finalAmt = isRepayment ? -Math.abs(amt) : Math.abs(amt);

    // Compute due date from tx date + grace period
    const txDate = new Date(newTx.date);
    const dueDateObj = new Date(txDate);
    dueDateObj.setDate(txDate.getDate() + (isRepayment ? 0 : newTx.gracePeriodDays));
    const dueDateStr = isRepayment ? '-' : dueDateObj.toISOString().split('T')[0];

    const txItem = {
      id: `tx-${Date.now()}`,
      date: newTx.date,
      type: newTx.type,
      amount: finalAmt,
      gracePeriodDays: isRepayment ? 0 : newTx.gracePeriodDays,
      dueDate: dueDateStr,
      repaymentApplied: isRepayment ? finalAmt : 0,
      interestBearing: isRepayment ? finalAmt : 0,
      notes: newTx.notes || (isRepayment ? 'Repayment applied to principal' : 'New spend under active grace period')
    };

    const updatedTransactions = [...(creditCard.transactions || []), txItem];
    const newBalance = Math.max(0, Math.round((balance + finalAmt) * 100) / 100);

    setCreditCard(prev => ({
      ...prev,
      balance: newBalance,
      transactions: updatedTransactions
    }));

    setIsAddTxModalOpen(false);
    setNewTx({
      date: config.referenceDate || new Date().toISOString().split('T')[0],
      type: 'New Loan / Spend',
      amount: '',
      gracePeriodDays: 30,
      notes: ''
    });
  };

  // Delete transaction
  const handleDeleteTransaction = (id) => {
    const tx = (creditCard.transactions || []).find(t => t.id === id);
    if (!tx) return;
    if (window.confirm('Delete this transaction record?')) {
      const updated = (creditCard.transactions || []).filter(t => t.id !== id);
      const newBalance = Math.max(0, Math.round((balance - tx.amount) * 100) / 100);
      setCreditCard(prev => ({
        ...prev,
        balance: newBalance,
        transactions: updated
      }));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      {/* Top Header & Overview */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
            <span className="bt-badge" style={{ background: '#ede9fe', color: '#6366f1', border: '1px solid #c7d2fe' }}>
              <FaCreditCard /> Credit Card & Interest Shield
            </span>
            <span className="bt-badge" style={{ background: '#ecfdf5', color: '#059669', border: '1px solid #a7f3d0' }}>
              Grace Period: {gracePeriodDays} Days
            </span>
          </div>
          <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800 }}>
            Credit Card Payment & Interest Optimizer
          </h2>
          <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.88rem' }}>
            Evaluate live interest rates, simulate repayment scenarios, and shield your cashflow before the next 30 days
          </p>
        </div>

        <button className="bt-btn-primary" onClick={() => setIsAddTxModalOpen(true)}>
          <FaPlus /> Log Card Transaction
        </button>
      </div>

      {/* Primary KPI Summary Cards */}
      <div className="bt-kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))' }}>
        {/* Card 1: Outstanding Balance */}
        <div className="bt-kpi-card" style={{ borderColor: '#e0e7ff' }}>
          <div className="bt-kpi-header">
            <span className="bt-kpi-title">CURRENT BALANCE</span>
            <div className="bt-kpi-icon danger">
              <FaCreditCard />
            </div>
          </div>
          <div>
            <div className="bt-kpi-value danger">{fmt(balance)}</div>
            <div className="bt-kpi-sub">
              Total credit obligation
            </div>
          </div>
        </div>

        {/* Card 2: Last Statement Interest */}
        <div className="bt-kpi-card">
          <div className="bt-kpi-header">
            <span className="bt-kpi-title">LAST STATEMENT INTEREST</span>
            <div className="bt-kpi-icon warning">
              <FaPercentage />
            </div>
          </div>
          <div>
            <div className="bt-kpi-value">{fmt(lastInterest)}</div>
            <div className="bt-kpi-sub">
              Charged on {creditCard.lastInterestDate || 'recent cycle'}
            </div>
          </div>
        </div>

        {/* Card 3: Implied APR & Monthly Rate */}
        <div className="bt-kpi-card">
          <div className="bt-kpi-header">
            <span className="bt-kpi-title">IMPLIED ANNUAL APR</span>
            <div className="bt-kpi-icon purple">
              <FaShieldAlt />
            </div>
          </div>
          <div>
            <div className="bt-kpi-value" style={{ color: '#6366f1' }}>
              {impliedAPR.toFixed(2)}%
            </div>
            <div className="bt-kpi-sub">
              Monthly: {impliedMonthlyRatePct.toFixed(2)}% | Daily: {dailyPeriodicRate.toFixed(3)}%
            </div>
          </div>
        </div>

        {/* Card 4: Projected Next Interest */}
        <div className="bt-kpi-card">
          <div className="bt-kpi-header">
            <span className="bt-kpi-title">PROJECTED NEXT INTEREST</span>
            <div className="bt-kpi-icon info">
              <FaClock />
            </div>
          </div>
          <div>
            <div className="bt-kpi-value" style={{ color: projectedNextInterest === 0 ? '#10b981' : '#dc2626' }}>
              {fmt(projectedNextInterest)}
            </div>
            <div className="bt-kpi-sub">
              At current planned repayment ({fmt(plannedRepayment)})
            </div>
          </div>
        </div>

        {/* Card 5: Estimated Interest Savings */}
        <div className="bt-kpi-card" style={{ borderColor: '#bbf7d0', backgroundColor: '#f0fdf4' }}>
          <div className="bt-kpi-header">
            <span className="bt-kpi-title" style={{ color: '#166534' }}>ESTIMATED SAVINGS</span>
            <div className="bt-kpi-icon success">
              <FaCheckCircle />
            </div>
          </div>
          <div>
            <div className="bt-kpi-value positive">{fmt(estimatedSavings)}</div>
            <div className="bt-kpi-sub" style={{ color: '#15803d' }}>
              Interest avoided this cycle
            </div>
          </div>
        </div>
      </div>

      {/* Smart 30-Day Recommendation Advisor */}
      <div className="bt-card" style={{ border: '1px solid #bfdbfe', backgroundColor: '#f8fafc' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff'
          }}>
            <FaLightbulb size={18} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800 }}>
              Smart 30-Day Recommended Payment Advisor
            </h3>
            <p className="bt-subtext" style={{ margin: 0 }}>
              Strategic repayment options to clear debt before the 30-day grace period closes and stop compounding interest
            </p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginTop: '14px' }}>
          {/* Strategy 1: Full Zero Interest Shield */}
          <div style={{
            background: '#ffffff',
            border: '2px solid #10b981',
            borderRadius: '14px',
            padding: '18px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.08)'
          }}>
            <div>
              <span className="bt-badge" style={{ background: '#ecfdf5', color: '#059669', marginBottom: '8px', fontWeight: 800 }}>
                RECOMMENDED: ZERO INTEREST
              </span>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', margin: '4px 0' }}>
                {fmt(balance)}
              </div>
              <p style={{ fontSize: '0.84rem', color: '#475569', lineHeight: 1.4, margin: '6px 0 14px 0' }}>
                Pay full balance before day 30 to completely eliminate upcoming interest charges (<strong>{fmt(lastInterest)}</strong> saved every month).
              </p>
            </div>
            <button
              className="bt-btn-primary"
              style={{ background: '#059669', justifyContent: 'center', fontSize: '0.85rem', padding: '8px 14px' }}
              onClick={() => updateCardField('plannedRepayment', balance)}
            >
              Adopt Full Payoff Plan <FaArrowRight />
            </button>
          </div>

          {/* Strategy 2: 50% High Impact Halver */}
          <div style={{
            background: '#ffffff',
            border: '1px solid #cbd5e1',
            borderRadius: '14px',
            padding: '18px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <div>
              <span className="bt-badge" style={{ background: '#eff6ff', color: '#2563eb', marginBottom: '8px' }}>
                50% ACCELERATED REDUCTION
              </span>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', margin: '4px 0' }}>
                {fmt(balance * 0.5)}
              </div>
              <p style={{ fontSize: '0.84rem', color: '#475569', lineHeight: 1.4, margin: '6px 0 14px 0' }}>
                Cuts next projected interest down to <strong>{fmt((balance * 0.5) * impliedMonthlyRate)}</strong> and slashes debt in 2 cycles.
              </p>
            </div>
            <button
              className="bt-btn-secondary"
              style={{ justifyContent: 'center', fontSize: '0.85rem', padding: '8px 14px' }}
              onClick={() => updateCardField('plannedRepayment', Math.round(balance * 0.5))}
            >
              Adopt 50% Plan <FaArrowRight />
            </button>
          </div>

          {/* Strategy 3: Disciplined $1,000 Step */}
          <div style={{
            background: '#ffffff',
            border: '1px solid #cbd5e1',
            borderRadius: '14px',
            padding: '18px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <div>
              <span className="bt-badge" style={{ background: '#fef3c7', color: '#d97706', marginBottom: '8px' }}>
                DISCIPLINED STEP ($1,000)
              </span>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', margin: '4px 0' }}>
                $1,000.00
              </div>
              <p style={{ fontSize: '0.84rem', color: '#475569', lineHeight: 1.4, margin: '6px 0 14px 0' }}>
                Saves <strong>{fmt(1000 * impliedMonthlyRate)}</strong> in interest immediately, keeping interest under {fmt(projectedNextInterest)}.
              </p>
            </div>
            <button
              className="bt-btn-secondary"
              style={{ justifyContent: 'center', fontSize: '0.85rem', padding: '8px 14px' }}
              onClick={() => updateCardField('plannedRepayment', 1000)}
            >
              Adopt $1,000 Plan <FaArrowRight />
            </button>
          </div>
        </div>
      </div>

      {/* Repayment Parameter Adjustment Panel */}
      <div className="bt-card">
        <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
          Interactive Statement Parameters & Repayment Slider
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '18px', marginBottom: '20px' }}>
          <div className="bt-form-group" style={{ margin: 0 }}>
            <label>Current Outstanding Balance ($)</label>
            <input
              type="number"
              step="0.01"
              className="bt-input"
              value={creditCard.balance}
              onChange={(e) => updateCardField('balance', parseFloat(e.target.value) || 0)}
            />
            <div className="bt-subtext">Update whenever credit card statement changes</div>
          </div>

          <div className="bt-form-group" style={{ margin: 0 }}>
            <label>Last Statement Interest ($)</label>
            <input
              type="number"
              step="0.01"
              className="bt-input"
              value={creditCard.lastInterest}
              onChange={(e) => updateCardField('lastInterest', parseFloat(e.target.value) || 0)}
            />
            <div className="bt-subtext">Interest charged on previous statement</div>
          </div>

          <div className="bt-form-group" style={{ margin: 0 }}>
            <label>Grace Period Window (Days)</label>
            <input
              type="number"
              className="bt-input"
              value={creditCard.gracePeriodDays || 30}
              onChange={(e) => updateCardField('gracePeriodDays', parseInt(e.target.value, 10) || 30)}
            />
            <div className="bt-subtext">Interest-free days before charges compound</div>
          </div>

          <div className="bt-form-group" style={{ margin: 0 }}>
            <label>Planned Repayment ($)</label>
            <input
              type="number"
              step="50"
              className="bt-input"
              value={creditCard.plannedRepayment}
              onChange={(e) => updateCardField('plannedRepayment', parseFloat(e.target.value) || 0)}
            />
            <div className="bt-subtext">Amount you plan to pay before due date</div>
          </div>
        </div>

        {/* Repayment Range Slider */}
        <div style={{ marginTop: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '8px' }}>
            <span>Simulate Repayment: <strong>{fmt(plannedRepayment)}</strong></span>
            <span style={{ color: '#059669', fontWeight: 700 }}>
              Interest Avoided: {fmt(estimatedSavings)} / month
            </span>
          </div>
          <input
            type="range"
            min="0"
            max={Math.max(1000, balance)}
            step="50"
            style={{ width: '100%', accentColor: '#4f46e5', cursor: 'pointer' }}
            value={plannedRepayment}
            onChange={(e) => updateCardField('plannedRepayment', parseFloat(e.target.value) || 0)}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>
            <span>$0 (Max Interest)</span>
            <span>50% ({fmt(balance * 0.5)})</span>
            <span>100% ({fmt(balance)})</span>
          </div>
        </div>
      </div>

      {/* Repayment Scenario Analyzer Table (Fixes Excel #VALUE! errors) */}
      <div className="bt-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
              Repayment Scenario Analyzer Matrix
            </h3>
            <p className="bt-subtext" style={{ margin: 0 }}>
              Live formula matrix comparing balance reductions, projected interest, and payoff speed
            </p>
          </div>
          <span className="bt-badge" style={{ background: '#ecfdf5', color: '#059669' }}>
            <FaCheckCircle /> Formulas Verified & Fixed
          </span>
        </div>

        <div className="bt-table-container">
          <table className="bt-table">
            <thead>
              <tr className="bt-table-header">
                <th>REPAYMENT AMOUNT</th>
                <th style={{ textAlign: 'right' }}>REMAINING BALANCE</th>
                <th style={{ textAlign: 'right' }}>PROJECTED NEXT INTEREST</th>
                <th style={{ textAlign: 'right' }}>MONTHLY INTEREST SAVED</th>
                <th style={{ textAlign: 'center' }}>PAYOFF HORIZON</th>
                <th style={{ textAlign: 'center' }}>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {scenarioTiers.map((tierAmount) => {
                const remBal = Math.max(0, balance - tierAmount);
                const nextInt = remBal * impliedMonthlyRate;
                const intSaved = Math.min(balance, tierAmount) * impliedMonthlyRate;
                const isCurrentPlan = Math.abs(plannedRepayment - tierAmount) < 1;
                const payoffMonths = tierAmount > 0 ? Math.ceil(balance / tierAmount) : 'Compounding';

                return (
                  <tr
                    key={tierAmount}
                    className="bt-table-row"
                    style={{
                      backgroundColor: isCurrentPlan ? 'rgba(79, 70, 229, 0.06)' : undefined,
                      fontWeight: isCurrentPlan ? 700 : 400
                    }}
                  >
                    <td className="bt-table-cell">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <strong style={{ color: tierAmount === balance ? '#059669' : '#0f172a' }}>
                          {fmt(tierAmount)}
                        </strong>
                        {tierAmount === balance && (
                          <span className="bt-badge" style={{ background: '#ecfdf5', color: '#059669', fontSize: '0.68rem', padding: '1px 6px' }}>
                            Full Payoff
                          </span>
                        )}
                        {isCurrentPlan && (
                          <span className="bt-badge" style={{ background: '#ede9fe', color: '#6366f1', fontSize: '0.68rem', padding: '1px 6px' }}>
                            Active Target
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="bt-table-cell" style={{ textAlign: 'right', fontWeight: 600 }}>
                      {fmt(remBal)}
                    </td>

                    <td className="bt-table-cell" style={{ textAlign: 'right', color: nextInt === 0 ? '#059669' : '#dc2626', fontWeight: 700 }}>
                      {fmt(nextInt)}
                    </td>

                    <td className="bt-table-cell" style={{ textAlign: 'right', color: '#059669', fontWeight: 700 }}>
                      +{fmt(intSaved)}
                    </td>

                    <td className="bt-table-cell" style={{ textAlign: 'center' }}>
                      <span style={{
                        fontSize: '0.82rem',
                        fontWeight: 600,
                        color: typeof payoffMonths === 'number' && payoffMonths <= 3 ? '#059669' : '#475569'
                      }}>
                        {typeof payoffMonths === 'number' ? `${payoffMonths} Month${payoffMonths === 1 ? '' : 's'}` : 'Indefinite'}
                      </span>
                    </td>

                    <td className="bt-table-cell" style={{ textAlign: 'center' }}>
                      <button
                        className="bt-btn-secondary"
                        style={{
                          padding: '4px 10px',
                          fontSize: '0.78rem',
                          borderColor: isCurrentPlan ? '#4f46e5' : '#cbd5e1',
                          color: isCurrentPlan ? '#4f46e5' : '#334155'
                        }}
                        onClick={() => updateCardField('plannedRepayment', tierAmount)}
                      >
                        {isCurrentPlan ? 'Selected' : 'Test Plan'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transaction Ledger Table (Matches rows 13-17 in Excel) */}
      <div className="bt-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
              Credit Card Transaction Ledger
            </h3>
            <p className="bt-subtext" style={{ margin: 0 }}>
              Chronological log of balances, loans/spending, and repayments with grace period status
            </p>
          </div>
          <button className="bt-btn-secondary" onClick={() => setIsAddTxModalOpen(true)}>
            <FaPlus /> Log Entry
          </button>
        </div>

        <div className="bt-table-container">
          <table className="bt-table">
            <thead>
              <tr className="bt-table-header">
                <th>DATE</th>
                <th>TYPE</th>
                <th style={{ textAlign: 'right' }}>AMOUNT ($)</th>
                <th style={{ textAlign: 'center' }}>GRACE PERIOD</th>
                <th>DUE / INTEREST-FREE DATE</th>
                <th style={{ textAlign: 'right' }}>REPAYMENT APPLIED</th>
                <th style={{ textAlign: 'right' }}>INTEREST BEARING</th>
                <th>STATUS / NOTES</th>
                <th style={{ textAlign: 'center' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {(creditCard.transactions || []).map((tx) => {
                return (
                  <tr key={tx.id} className="bt-table-row">
                    <td className="bt-table-cell" style={{ fontWeight: 600 }}>
                      {tx.date}
                    </td>

                    <td className="bt-table-cell">
                      <span className="bt-badge" style={{
                        background: tx.type === 'Repayment' ? '#ecfdf5' : '#eff6ff',
                        color: tx.type === 'Repayment' ? '#059669' : '#2563eb'
                      }}>
                        {tx.type}
                      </span>
                    </td>

                    <td className="bt-table-cell" style={{
                      textAlign: 'right',
                      fontWeight: 700,
                      color: tx.type === 'Repayment' ? '#059669' : '#0f172a'
                    }}>
                      {fmt(tx.amount)}
                    </td>

                    <td className="bt-table-cell" style={{ textAlign: 'center' }}>
                      {tx.gracePeriodDays > 0 ? `${tx.gracePeriodDays} Days` : '-'}
                    </td>

                    <td className="bt-table-cell">
                      {tx.dueDate}
                    </td>

                    <td className="bt-table-cell" style={{ textAlign: 'right', fontWeight: 600 }}>
                      {fmt(tx.repaymentApplied)}
                    </td>

                    <td className="bt-table-cell" style={{ textAlign: 'right', fontWeight: 700, color: tx.interestBearing > 0 ? '#dc2626' : '#475569' }}>
                      {fmt(tx.interestBearing)}
                    </td>

                    <td className="bt-table-cell" style={{ fontSize: '0.82rem', color: '#64748b', maxWidth: '280px' }}>
                      {tx.notes}
                    </td>

                    <td className="bt-table-cell" style={{ textAlign: 'center' }}>
                      <button
                        className="bt-icon-btn danger"
                        title="Delete entry"
                        onClick={() => handleDeleteTransaction(tx.id)}
                      >
                        <FaTrash size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Transaction Modal */}
      {isAddTxModalOpen && (
        <div className="bt-modal-backdrop" onClick={() => setIsAddTxModalOpen(false)}>
          <div className="bt-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="bt-modal-header">
              <h3>Log Credit Card Transaction</h3>
              <button className="bt-icon-btn" onClick={() => setIsAddTxModalOpen(false)}>
                &times;
              </button>
            </div>

            <form onSubmit={handleAddTransaction}>
              <div className="bt-modal-body">
                <div className="bt-form-row">
                  <div className="bt-form-group">
                    <label>Transaction Date *</label>
                    <input
                      type="date"
                      className="bt-input"
                      value={newTx.date}
                      onChange={(e) => setNewTx({ ...newTx, date: e.target.value })}
                      required
                    />
                  </div>

                  <div className="bt-form-group">
                    <label>Transaction Type *</label>
                    <select
                      className="bt-select"
                      value={newTx.type}
                      onChange={(e) => setNewTx({ ...newTx, type: e.target.value })}
                    >
                      <option value="New Loan / Spend">New Loan / Spend (+)</option>
                      <option value="Repayment">Repayment (-)</option>
                      <option value="Opening Balance">Opening Balance</option>
                    </select>
                  </div>
                </div>

                <div className="bt-form-row">
                  <div className="bt-form-group">
                    <label>Amount ($) *</label>
                    <input
                      type="number"
                      step="0.01"
                      className="bt-input"
                      placeholder="0.00"
                      value={newTx.amount}
                      onChange={(e) => setNewTx({ ...newTx, amount: e.target.value })}
                      required
                    />
                  </div>

                  <div className="bt-form-group">
                    <label>Grace Period (Days)</label>
                    <input
                      type="number"
                      className="bt-input"
                      value={newTx.gracePeriodDays}
                      disabled={newTx.type === 'Repayment'}
                      onChange={(e) => setNewTx({ ...newTx, gracePeriodDays: parseInt(e.target.value, 10) || 0 })}
                    />
                  </div>
                </div>

                <div className="bt-form-group">
                  <label>Notes / Merchant Description</label>
                  <input
                    type="text"
                    className="bt-input"
                    placeholder="e.g. Card payment against balance or supplier purchase"
                    value={newTx.notes}
                    onChange={(e) => setNewTx({ ...newTx, notes: e.target.value })}
                  />
                </div>
              </div>

              <div className="bt-modal-footer">
                <button
                  type="button"
                  className="bt-btn-secondary"
                  onClick={() => setIsAddTxModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="bt-btn-primary">
                  Save Transaction
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditCardTracker;
